// src/pages/Account/ProfileSettings.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountryList } from '../../services/chatApi';


const ProfileSettings = ({ userData, onUpdate }) => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        date_of_birth: '',
        activity_field: ''
    });

    const [countries, setCountries] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const lang = i18n.language.split('-')[0]; // 'en', 'de', or 'fr'
                const countryList = await getCountryList(lang);
                setCountries(countryList);
            } catch (error) {
                console.error("Failed to fetch country list:", error);
                // Set a fallback list or show an error
                setCountries([{ code: 'DE', name: 'Germany' }]);
            }
        };

        fetchCountries();
    }, [i18n.language]); // Refetch if the user changes the language


    useEffect(() => {
        console.log("User data received in ProfileSettings:", userData);
        if (userData) {
            setFormData({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                phone_number: userData.phone_number || '',
                address: userData.address || '',
                city: userData.city || '',
                state: userData.state || '',
                postal_code: userData.postal_code || '',
                country: userData.country || '', // e.g., "DE"
                date_of_birth: userData.date_of_birth || '',
                activity_field: userData.activity_field || ''
            });
        }
    }, [userData]);

    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(formData);
    };

    // A simplified list. You could fetch this from an API or use a library.
    

    return (
    <form onSubmit={handleSubmit}>
        <h2 className="text-xl font-semibold mb-4">{t('account.profile.title')}</h2>
        
        {/* Account Information Section (Read-only) */}
        <div className="mb-8 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h3 className="text-lg font-semibold mb-4 text-slate-300">{t('account.accountInfo')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="input-label">{t('email')}</label>
                    <input 
                        value={userData?.email || ''} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                <div>
                    <label className="input-label">{t('credits')}</label>
                    <input 
                        value={userData?.credits || 0} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                <div>
                    <label className="input-label">{t('accountStatus')}</label>
                    <input 
                        value={userData?.is_verified ? t('verified') : t('notVerified')} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                <div>
                    <label className="input-label">{t('subscriptionPlan')}</label>
                    <input 
                        value={userData?.subscription_plan || t('none')} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                <div>
                    <label className="input-label">{t('subscriptionStatus')}</label>
                    <input 
                        value={userData?.subscription_status || t('none')} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                {userData?.has_active_trial && (
                    <div>
                        <label className="input-label">{t('trialExpiresAt')}</label>
                        <input 
                            value={userData?.trial_expires_at ? new Date(userData.trial_expires_at).toLocaleString() : ''} 
                            disabled 
                            className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                        />
                    </div>
                )}
                <div>
                    <label className="input-label">{t('memberSince')}</label>
                    <input 
                        value={userData?.created_at ? new Date(userData.created_at).toLocaleDateString() : ''} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
                <div>
                    <label className="input-label">{t('country')}</label>
                    <input 
                        value={userData?.country_name || userData?.country || ''} 
                        disabled 
                        className="input-style mt-1 bg-slate-700/50 text-slate-400 cursor-not-allowed" 
                    />
                </div>
            </div>
        </div>

        {/* Editable Profile Information Section */}
        <div className="mb-4">
            <h3 className="text-lg font-semibold mb-4 text-slate-300">{t('account.personalInfo')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div>
                    <label className="input-label" htmlFor="first_name">{t('firstName')}</label>
                    <input 
                        id="first_name" 
                        name="first_name" 
                        value={formData.first_name} 
                        onChange={handleChange} 
                        className="input-style mt-1 text-black" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="last_name">{t('lastName')}</label>
                    <input 
                        id="last_name" 
                        name="last_name" 
                        value={formData.last_name} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="date_of_birth">{t('dateOfBirth')}</label>
                    <input 
                        id="date_of_birth" 
                        name="date_of_birth" 
                        type="date" 
                        value={formData.date_of_birth} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="phone_number">{t('phoneNumber')}</label>
                    <input 
                        id="phone_number" 
                        name="phone_number" 
                        value={formData.phone_number} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="activity_field">{t('activityField')}</label>
                    <select 
                        id="activity_field" 
                        name="activity_field" 
                        value={formData.activity_field} 
                        onChange={handleChange} 
                        className="input-style mt-1"
                    >
                        <option value="">{t('selectActivityField')}</option>
                        <option value="SCHOOL_STUDENT">{t('activityFields.schoolStudent')}</option>
                        <option value="SCHOOL_TEACHER">{t('activityFields.schoolTeacher')}</option>
                        <option value="UNIVERSITY_STUDENT">{t('activityFields.universityStudent')}</option>
                        <option value="UNIVERSITY_STAFF">{t('activityFields.universityStaff')}</option>
                        <option value="MEDIA_MARKETING">{t('activityFields.mediaMarketing')}</option>
                        <option value="SOCIAL_HEALTHCARE">{t('activityFields.socialHealthcare')}</option>
                        <option value="ADMINISTRATION">{t('activityFields.administration')}</option>
                        <option value="BUSINESS">{t('activityFields.business')}</option>
                        <option value="PERSONAL">{t('activityFields.personal')}</option>
                        <option value="OTHER">{t('activityFields.other')}</option>
                    </select>
                </div>

                {/* Address Information */}
                <div className="md:col-span-2">
                    <label className="input-label" htmlFor="address">{t('address')}</label>
                    <input 
                        id="address" 
                        name="address" 
                        value={formData.address} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="city">{t('city')}</label>
                    <input 
                        id="city" 
                        name="city" 
                        value={formData.city} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="state">{t('state')}</label>
                    <input 
                        id="state" 
                        name="state" 
                        value={formData.state} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                <div>
                    <label className="input-label" htmlFor="postal_code">{t('postalCode')}</label>
                    <input 
                        id="postal_code" 
                        name="postal_code" 
                        value={formData.postal_code} 
                        onChange={handleChange} 
                        className="input-style mt-1" 
                    />
                </div>
                
            </div>
        </div>

        <button type="submit" className="btn-primary mt-6 hover:bg-brand-orange p-2">{t('saveChanges')}</button>
    </form>
);
};
// Add a generic .input-style class in your index.css:
// .input-style { @apply w-full p-2 bg-slate-700 border border-slate-600 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none; }

export default ProfileSettings;